import { createRouter, createRootRoute, createRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Products } from './pages/Products';
import { POS } from './pages/POS';
import { Invoices } from './pages/Invoices';
import { Customers } from './pages/Customers';
import { Suppliers } from './pages/Suppliers';
import { PurchaseOrders } from './pages/PurchaseOrders';
import { Reports } from './pages/Reports';
import { Documentation } from './pages/Documentation';
import { CashManagement } from './pages/CashManagement';
import { Admin } from './pages/Admin';
import { Backups } from './pages/Backups';
import { PurchaseOrderDetail } from './pages/PurchaseOrderDetail';
import { SysAdminApp, SysAdminLogin } from './pages/SysAdminApp';

// Wrapper that applies Layout to all authenticated pages + role guard
function tokenExpired(): boolean {
  const t = localStorage.getItem('accessToken');
  if (!t) return true;
  try {
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now() + 5000;
  } catch {
    return true;
  }
}

function ProtectedLayout() {
  const [authState, setAuthState] = useState<'checking' | 'ready'>('checking');
  const location = useLocation();

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem('accessToken')) {
        window.location.href = '/login';
        return;
      }
      // Valid token → render. Expired/invalid → try refresh once; on failure
      // clean logout. Never shows the app shell with dead credentials.
      if (!tokenExpired()) {
        setAuthState('ready');
        return;
      }
      const rt = localStorage.getItem('refreshToken');
      if (!rt) {
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        return;
      }
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
          },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) throw new Error('refresh failed');
        const d = await res.json();
        localStorage.setItem('accessToken', d.accessToken);
        localStorage.setItem('refreshToken', d.refreshToken);
        setAuthState('ready');
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantSlug');
        window.location.href = '/login';
      }
    })();
  }, []);

  if (authState === 'checking') {
    // Clean loading screen — NOT the app shell/skeleton.
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-3">🏪</div>
          <p className="text-gray-500 dark:text-gray-400">Cargando...</p>
        </div>
      </div>
    );
  }

  const token = localStorage.getItem('accessToken');
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const role = user?.role || 'cashier';

  const restrictedPaths: Record<string, string[]> = {
    '/products': ['admin', 'manager'],
    '/customers': ['admin', 'manager'],
    '/suppliers': ['admin', 'manager'],
    '/purchase-orders': ['admin', 'manager'],
    '/invoices': ['admin', 'manager'],
    '/reports': ['admin', 'manager'],
    '/admin': ['admin', 'manager'],
    '/backups': ['admin'],
  };

  for (const [path, allowedRoles] of Object.entries(restrictedPaths)) {
    if (location.pathname.startsWith(path) && !allowedRoles.includes(role)) {
      return (
        <Layout>
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-2">⛔ Acceso Denegado</h2>
            <p className="text-gray-600">No tenés permisos para ver esta página.</p>
            <p className="text-sm text-gray-400 mt-2">Rol: {role}</p>
          </div>
        </Layout>
      );
    }
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const rootRoute = createRootRoute({
  notFoundComponent: () => (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626' }}>404 - Página no encontrada</h2>
      <p style={{ color: '#6b7280', marginTop: '8px' }}>La ruta solicitada no existe.</p>
      <button onClick={() => window.location.href = '/'} style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
        Volver al inicio
      </button>
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

const sysadminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sysadmin/login',
  component: SysAdminLogin,
});

const sysadminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sysadmin',
  component: SysAdminApp,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: Dashboard,
});

const productsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products',
  component: Products,
});

const posRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pos',
  component: POS,
});

const invoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices',
  component: Invoices,
});

const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers',
  component: Customers,
});

const suppliersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers',
  component: Suppliers,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/purchase-orders',
  component: PurchaseOrders,
});

const purchaseOrdersDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/purchase-orders/$id',
  component: PurchaseOrderDetail,
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: Reports,
});

const documentationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/documentation',
  component: Documentation,
});

const cashRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cash',
  component: CashManagement,
});

const adminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin',
  component: Admin,
});

const backupsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/backups',
  component: Backups,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  sysadminLoginRoute,
  sysadminRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    productsRoute,
    posRoute,
    invoicesRoute,
    customersRoute,
    suppliersRoute,
    purchaseOrdersRoute,
    purchaseOrdersDetailRoute,
    reportsRoute,
    documentationRoute,
    cashRoute,
    adminRoute,
    backupsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router; }
}
